import ActionCard from "@/components/ActionCard";
import type { MaterialNames } from "@/components/material_symbol_names";
import { useCanManageCommitteesBool, useCanViewCommitteesBool } from "@/hooks/useCapabilities";
import { useFlags } from "@/hooks/useFeatureFlags";
import usePageTitle from "@/hooks/usePageTitle";
import {
  isAchievementsReportEnabled,
  isApplicationsMembershipEnabled
} from "@/pages/people/_membershipServicesNavLinks";
import { COMMITTEES_BASE_PATH } from "@/pages/people/membership-services/committees/constants";
import type { Path } from "@/router";
import { useNavigate } from "@/router";
import TabLayout from "../_TabLayout";

type MembershipServiceCard = {
  title: string;
  description: string;
  iconName: MaterialNames;
  to: Path;
};

const APPLICATIONS_CARD: MembershipServiceCard = {
  title: "Manage Applications",
  description: "Review and process all types of membership program applications",
  iconName: "assignment_ind",
  to: "/people/membership-services/applications/all"
};

const MEMBER_ELECTIONS_CARD: MembershipServiceCard = {
  title: "Manage Member Elections",
  description: "View people who are ready to be elected to membership and manually process the elections",
  iconName: "badge",
  to: "/people/membership-services/member-elections"
};

const ACHIEVEMENTS_REPORT_CARD: MembershipServiceCard = {
  title: "View Achievements",
  description: "View a list of awards and certifications earned by people",
  iconName: "emoji_events",
  to: "/people/membership-services/achievements-report"
};

export default function MembershipServicesPage() {
  usePageTitle("/people/membership-services", "People | Operations");
  const navigate = useNavigate();
  const flags = useFlags();
  const canViewCommittees = useCanViewCommitteesBool();
  const canManageCommittees = useCanManageCommitteesBool();

  const showManageCommitteesCopy = canManageCommittees;
  const committeeManagementCard: MembershipServiceCard = {
    title: showManageCommitteesCopy ? "Manage Committees" : "View Committees",
    description: showManageCommitteesCopy
      ? "Manage PGA National committees and committee participants"
      : "View PGA National committees and committee participants",
    iconName: "groups",
    to: COMMITTEES_BASE_PATH
  };

  const cards = [
    APPLICATIONS_CARD,
    ...(isAchievementsReportEnabled(flags) ? [ACHIEVEMENTS_REPORT_CARD] : []),
    ...(isApplicationsMembershipEnabled(flags) ? [MEMBER_ELECTIONS_CARD] : []),
    ...(canViewCommittees ? [committeeManagementCard] : [])
  ];

  return (
    <TabLayout>
      <div className="w-full">
        <p className="text-body-medium mb-6 mt-12">Select an operations area below to get started.</p>
        <div className="grid w-full grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6" data-testid="operations-cards">
          {cards.map((card) => (
            <div key={card.title} className="min-w-0">
              <ActionCard
                actionFn={() => navigate(card.to)}
                iconName={card.iconName}
                text={card.description}
                title={card.title}
                className="w-full max-w-none"
              />
            </div>
          ))}
        </div>
      </div>
    </TabLayout>
  );
}
